
type Settings = {
  customVoteStorageKey: string;
}

type Votes = {
  /** Array of article ids */
  down: string[];
  /** Array of article ids */
  up: string[];
};

type Vote = 'up' | 'down';

export default class CustomerService {
  settings: Settings;
  dom: {
    root: HTMLElement; // Entire customer service page essentially
    voteSection: HTMLElement | null;
    voteBtns: Array<HTMLButtonElement>;
    usefulVotesCount: HTMLElement | null;
    totalVotesCount: HTMLElement | null;
  };
  votes: Votes = {
    up: [],
    down: [],
  };

  constructor(element: HTMLElement, options: Partial<Settings> = {}) {
    const defaultSettings: Settings = {
      customVoteStorageKey: 'cs-article-votes',
    };

    this.settings = Object.assign({}, defaultSettings, options);

    this.dom = {
      root: element,
      voteSection: element.querySelector('[data-cs-custom-voting]'),
      voteBtns: Array.from(element.querySelectorAll('[data-cs-vote]')),
      usefulVotesCount: element.querySelector('[data-cs-useful-votes]'),
      totalVotesCount: element.querySelector('[data-cs-total-votes]'),
    };

    this.initCustomRating();
  }

  initCustomRating() {
    if(this.dom.voteSection && this.dom.voteBtns.length) {

      // Set previous voting state from localStorage
      const useStorage = window.localStorage ? true : false;
      const articleId = this.dom.voteSection.dataset.articleId;
      const storedVotes = window.localStorage.getItem(this.settings.customVoteStorageKey);
      let voted: string | boolean = false;

      if(!useStorage || !articleId) {
        // Cannot vote without storage or article id, hide rating element
        this.dom.voteSection.style.display = 'none';
        return;
      }

      // Parse votes from localStorage
      if(storedVotes) {
        try {
          this.votes = JSON.parse(storedVotes);
        } catch(e) {
          console.error('[customerService]: Could not parse votes from localStorage:', e);
        }
      }

      if(this.votes?.up && this.votes?.down) {
        if(~this.votes.up.indexOf(articleId)) {
          voted = 'up';
        } else if(~this.votes.down.indexOf(articleId)) {
          voted = 'down';
        }
      }

      if(voted) {
        // Make previous vote seen
        if(voted === 'up') {
          Array.from(this.dom.voteBtns).find(btn => btn.dataset.csVote === 'up')?.classList.toggle('voted');
        } else if(voted === 'down') {
          Array.from(this.dom.voteBtns).find(btn => btn.dataset.csVote === 'down')?.classList.toggle('voted');
        }

        // Disable both buttons
        this.dom.voteBtns.forEach(btn => {
          btn.disabled = true;
        });
      }

      // Add event listeners to voting buttons
      for(let btn of this.dom.voteBtns) {
        btn.addEventListener('click', this.onVoteBtnClick.bind(this));
      }
    }
  }

  onVoteBtnClick(event: Event) {
    const targetBtn = event.target as HTMLButtonElement;
    const vote = targetBtn.dataset.csVote as Vote;
    const articleId = this.dom.voteSection?.dataset.articleId;

    console.log('Voted:', vote, 'Article ID:', articleId);

    if(targetBtn.disabled) {
      return;
    }

    if(vote && articleId) {
      // Disable buttons
      this.dom.voteBtns.forEach(btn => {
        btn.disabled = true;
      });

      targetBtn.classList.add('loading');

      // Send vote
      this.sendVote(vote)
        .then(response => {
          // Response is object or false
          if(response) {
            targetBtn.classList.add('voted');

            this.votes[vote].push(articleId);
            window.localStorage.setItem(this.settings.customVoteStorageKey, JSON.stringify(this.votes));

            this.updateVoteCounts(vote);
          }

          targetBtn.classList.remove('loading');
        })
        .catch(error => {
          console.error('[customerService]: Error sending vote:', error);

          // Re-enable buttons
          this.dom.voteBtns.forEach(btn => {
            btn.disabled = false;
          });

          targetBtn.classList.remove('loading');
        });
    }
  }

  /**
   * Updates vote counts on page
   * ( "1/5 found this useful" )
   */
  updateVoteCounts(vote: Vote) {
    if(this.dom.usefulVotesCount && this.dom.totalVotesCount) {
      const usefulVotes = parseInt(this.dom.usefulVotesCount.textContent || '0', 10);
      const totalVotes = parseInt(this.dom.totalVotesCount.textContent || '0', 10);

      if(vote === 'up') {
        this.dom.usefulVotesCount.textContent = (usefulVotes + 1).toString();
        this.dom.totalVotesCount.textContent = (totalVotes + 1).toString();
      } else if(vote === 'down') {
        this.dom.totalVotesCount.textContent = (totalVotes + 1).toString();
      }
    }
  }

  /**
   * Sends vote to server
   */
  sendVote(vote: Vote): Promise<Response> {
    if(this.dom.voteSection?.dataset.articleId) {
      return fetch('/stage-ajax/cs_votes/', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: this.dom.voteSection.dataset.articleId,
          vote: vote,
        }),
      })
        .then(response => {
          if(response.ok) {
            return response.json();
          } else {
            return Promise.reject('Server responded with error');
          }
        });
    } else {
      return Promise.reject('No article id');
    }
  }
}

export function init(elements: NodeListOf<HTMLElement>) {
  console.log('customerService.init()');
  for (let element of elements) {
    new CustomerService(element);
  }
}
